/** A single row of the printed fee defaulters list. */
export interface FeeDefaulterPrintRow {
    studentName: string;
    rollNumber?: string | null;
    classSection?: string | null;
    fatherName?: string | null;
    motherName?: string | null;
    address?: string | null;
    contact?: string | null;
    totalOverdueAmount: number;
    /** e.g. "INV-2025-000318 (due 10 May 2026)" — pre-joined so the printer stays dumb. */
    overdueInvoiceLabels: string[];
}

export interface FeeDefaultersPrintData {
    schoolName?: string;
    /** The class the list was filtered to — always set, since the API requires it. */
    className: string;
    /** The section, when one was picked. Omitted from the header entirely when absent. */
    sectionName?: string | null;
    rows: FeeDefaulterPrintRow[];
}

/** Row values are interpolated into markup, so they must be escaped. */
function esc(v: string | number | null | undefined): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Missing values print as an em dash rather than an empty cell. */
function cell(v: string | null | undefined): string {
    const s = (v ?? '').trim();
    return s.length > 0 ? esc(s) : '<span class="muted">&mdash;</span>';
}

const clean = (v: string | null | undefined): string | null => {
    const s = (v ?? '').trim();
    return s.length > 0 ? s : null;
};

function formatCurrency(n: number): string {
    return 'Rs. ' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Builds the print document. Split out from `printFeeDefaulters` so the markup can
 *  be rendered and inspected without a browser. */
export function buildFeeDefaultersHtml(data: FeeDefaultersPrintData): string {
    const printedOn = new Date().toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
    });

    const totalOverdue = data.rows.reduce((sum, r) => sum + r.totalOverdueAmount, 0);

    const rowsHtml = data.rows.map((r, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="name">${cell(r.studentName)}</td>
        <td class="num">${cell(r.rollNumber)}</td>
        <td>${cell(r.fatherName)}</td>
        <td>${cell(r.motherName)}</td>
        <td class="address">${cell(r.address)}</td>
        <td class="num">${cell(r.contact)}</td>
        <td class="invoices">${r.overdueInvoiceLabels.length > 0 ? r.overdueInvoiceLabels.map(esc).join('<br>') : cell(null)}</td>
        <td class="num amount">${formatCurrency(r.totalOverdueAmount)}</td>
      </tr>`).join('');

    const section = clean(data.sectionName);

    const field = (label: string, value: string) => `
      <div class="field">
        <span class="field__label">${esc(label)}</span>
        <span class="field__value">${esc(value)}</span>
      </div>`;

    const fieldsHtml = [
        field('Class', data.className),
        section ? field('Section', section) : '',
        field('Defaulters', String(data.rows.length)),
        field('Total Overdue', formatCurrency(totalOverdue)),
        field('Printed', printedOn),
    ].join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Fee Defaulters</title>
<style>
  /* A4 landscape: 297mm x 210mm. With 10mm side margins the sheet has 277mm of
     usable width, which the table fills exactly (column widths total 100%). */
  @page { size: A4 landscape; margin: 10mm 10mm 12mm; }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #0f172a;
    font-size: 9.5px;
    line-height: 1.35;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ---- Header ---- */
  .header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 6px;
    margin-bottom: 8px;
    border-bottom: 2px solid #0f172a;
  }
  .brand h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  .brand .doc {
    margin: 1px 0 0;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }

  .fields { display: flex; align-items: flex-end; gap: 14px; }
  .field { display: flex; flex-direction: column; gap: 1px; }
  .field__label {
    font-size: 6.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
  }
  .field__value {
    font-size: 10px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
  }

  /* ---- Table ---- */
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }

  thead { display: table-header-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }

  th {
    text-align: left;
    font-size: 7px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #475569;
    background: #f1f5f9;
    padding: 4px 5px;
    border-bottom: 1px solid #cbd5e1;
  }
  td {
    padding: 4px 5px;
    border-bottom: 1px solid #e8edf3;
    vertical-align: top;
    overflow-wrap: anywhere;
  }
  tbody tr:nth-child(even) td { background: #f8fafc; }

  .name { font-weight: 600; }
  .num { font-variant-numeric: tabular-nums; }
  .muted { color: #94a3b8; }
  .address { color: #334155; }
  .invoices { color: #334155; font-size: 8.5px; }
  .amount { font-weight: 700; color: #b91c1c; text-align: right; }
  th.amount { text-align: right; }

  .c-sno { width: 2%; }
  .c-name { width: 12%; }
  .c-roll { width: 4%; }
  .c-father { width: 10%; }
  .c-mother { width: 10%; }
  .c-address { width: 14%; }
  .c-contact { width: 8%; }
  .c-invoices { width: 22%; }
  .c-amount { width: 18%; }

  .footer {
    margin-top: 8px;
    padding-top: 5px;
    border-top: 1px dashed #cbd5e1;
    font-size: 7.5px;
    color: #94a3b8;
    display: flex;
    justify-content: space-between;
  }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <h1>${esc(data.schoolName || 'Gurukul Academy')}</h1>
      <p class="doc">Fee Defaulters List</p>
    </div>
    <div class="fields">${fieldsHtml}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="c-sno">#</th>
        <th class="c-name">Student Name</th>
        <th class="c-roll">Roll No.</th>
        <th class="c-father">Father's Name</th>
        <th class="c-mother">Mother's Name</th>
        <th class="c-address">Address</th>
        <th class="c-contact">Contact</th>
        <th class="c-invoices">Overdue Invoices</th>
        <th class="c-amount amount">Total Overdue</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>

  <div class="footer">
    <span>${esc(data.schoolName || 'Gurukul Academy')} &middot; Printed on ${esc(printedOn)}</span>
    <span>${data.rows.length} defaulter${data.rows.length === 1 ? '' : 's'} &middot; ${formatCurrency(totalOverdue)} total overdue</span>
  </div>
<script>
  window.onload = function () { window.print(); };
<${'/'}script>
</body>
</html>`;

    return html;
}

export function printFeeDefaulters(data: FeeDefaultersPrintData) {
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (w) {
        w.document.write(buildFeeDefaultersHtml(data));
        w.document.close();
    }
}
